
import { Component, Prop, Vue } from 'vue-property-decorator';
import Icon from '@/layouts/Icon.vue';
import OrderApiConfig from '@/api/OrderApiConfig';
import store from '@/store';
import {
  JpCoBenefitoneBsdpBffPresentationResponseBenepoGetResponse,
  DPApi,
  JpCoBenefitoneBsSubsidyDtoErrorData,
  JpCoBenefitoneBsPointDtoInlineResponse400,
  PutBenepoRequest,
} from '@/openapi/order';
import ErrorForApiComponent from '@/components/ErrorForApiComponent.vue';
import EnvRepository from '@/repository/EnvRepository';
import ApiService from '@/services/ApiService';

type UsePointType = 'All' | 'Partial' | 'None';
type UsePointErrorType = 'NoError' | 'Blank' | 'NotPositive' | 'NotNum' | 'Exceed' | 'Over';

@Component({
  components: {
    ErrorForApiComponent,
    Icon,
  },
  filters: {
    comma(value?: number) {
      if (!value) return 0;
      return value.toLocaleString();
    },
  },
})
export default class Benp01View extends Vue {
  //* ***********************************************************************************/
  // Props
  //* ***********************************************************************************/
  @Prop() public orderSite: number;

  //* ***********************************************************************************/
  // Data
  //* ***********************************************************************************/
  benepoGetResponse: JpCoBenefitoneBsdpBffPresentationResponseBenepoGetResponse = {};

  commonErrorResponse: JpCoBenefitoneBsSubsidyDtoErrorData = { errorCode: '', errorDetail: '' };

  benepoErrorResponse: JpCoBenefitoneBsPointDtoInlineResponse400 = {
    errorCode: '',
    errorDetail: '',
  };

  optionUsePoint: Array<{ value: UsePointType; label: string }> = [
    { value: 'All', label: 'すべてのベネポを利用する' },
    { value: 'Partial', label: '一部のベネポを利用する' },
    { value: 'None', label: '利用しない' },
  ];

  selectedUsePoint: UsePointType = 'All';

  form: {
    usePoint: {
      value: number | '';
      errorType: UsePointErrorType;
    };
  } = {
    usePoint: {
      value: 0,
      errorType: 'NoError',
    },
  };

  //* ***********************************************************************************/
  // Computed
  //* ***********************************************************************************/
  get disabledInputUsePoint(): boolean {
    return ['All', 'None'].includes(this.selectedUsePoint);
  }

  get disabledSubmit(): boolean {
    return this.selectedUsePoint === 'Partial' && this.form.usePoint.errorType !== 'NoError';
  }

  get getErrorBlank(): string {
    return EnvRepository.getEnv().VUE_APP_BENP01_ERR_BLANK;
  }

  get getErrorNotPositive(): string {
    return EnvRepository.getEnv().VUE_APP_BENP01_ERR_NEGATIVE;
  }

  get getErrorNum(): string {
    return EnvRepository.getEnv().VUE_APP_BENP01_ERR_NUM;
  }

  get getErrorExceed(): string {
    return EnvRepository.getEnv().VUE_APP_BENP01_ERR_EXCEED;
  }

  get getErrorOver(): string {
    return EnvRepository.getEnv().VUE_APP_BENP01_ERR_OVER;
  }

  get getUsePointForAll(): number {
    // validate.
    if (!this.benepoGetResponse.billingAmount || !this.benepoGetResponse.canUsePoint) {
      return 0;
    }
    return this.benepoGetResponse.billingAmount > this.benepoGetResponse.canUsePoint
      ? this.benepoGetResponse.canUsePoint
      : this.benepoGetResponse.billingAmount;
  }

  //* ***********************************************************************************/
  // 初期表示
  //* ***********************************************************************************/
  mounted(): void {
    if (this.orderSite === 4) {
      this.doApiGetBenepoFellowTravelers();
    } else {
      this.doApiGetBenepo();
    }
  }

  doApiGetBenepo(): void {
    const config = OrderApiConfig.newInstance();
    const apiOrder = new DPApi(config);
    store.commit('setIsLoading', true);

    apiOrder
      .getBenepo()
      .then((res) => {
        this.benepoGetResponse = res;

        if (this.$store.state.benp01SelectedUsePoint) {
          // reset
          if (res.benepoAmountTotal === 0) {
            this.selectedUsePoint = 'All';
            this.$store.commit('setBenp01SelectedUsePoint', 'All');
            this.form.usePoint.value = this.getUsePointForAll;
            return;
          }
          // restore.
          this.selectedUsePoint = this.$store.state.benp01SelectedUsePoint as UsePointType;
          this.form.usePoint.value = res.benepoAmountTotal ?? 0;
        } else {
          // init.
          this.form.usePoint.value = this.getUsePointForAll;
        }
      })
      .catch((error: unknown) => {
        if (ApiService.isErrorData(error)) {
          this.commonErrorResponse = error;
        } else {
          this.$router.push({
            name: 'Erro01',
          });
        }
      })
      .finally(() => {
        store.commit('setIsLoading', false);
      });
  }

  doApiGetBenepoFellowTravelers(): void {
    const config = OrderApiConfig.newInstance();
    const apiOrder = new DPApi(config);
    store.commit('setIsLoading', true);

    apiOrder
      .getBenepoFellowTravelers()
      .then((res) => {
        this.benepoGetResponse = res;

        if (this.$store.state.benp01SelectedUsePoint) {
          // reset
          if (res.benepoAmountTotal === 0) {
            this.selectedUsePoint = 'All';
            this.$store.commit('setBenp01SelectedUsePoint', 'All');
            this.form.usePoint.value = this.getUsePointForAll;
            return;
          }
          // restore.
          this.selectedUsePoint = this.$store.state.benp01SelectedUsePoint as UsePointType;
          this.form.usePoint.value = res.benepoAmountTotal ?? 0;
        } else {
          // init.
          this.form.usePoint.value = this.getUsePointForAll;
        }
      })
      .catch((error: unknown) => {
        if (ApiService.isErrorData(error)) {
          this.commonErrorResponse = error;
        } else {
          this.$router.push({
            name: 'Erro01',
          });
        }
      })
      .finally(() => {
        store.commit('setIsLoading', false);
      });
  }

  //* ***********************************************************************************/
  // Methods
  //* ***********************************************************************************/
  private validateUsePoint(newValue: number | ''): void {
    let errorType: UsePointErrorType = 'NoError';

    if (newValue === '') {
      errorType = 'Blank';
    } else if (newValue < 1) {
      errorType = 'NotPositive';
    } else if (newValue.toString().match(/^[0-9]+\.[0-9]+$/)) {
      errorType = 'NotNum';
    } else if (this.benepoGetResponse.canUsePoint !== undefined && newValue > this.benepoGetResponse.canUsePoint) {
      errorType = 'Exceed';
    } else if (this.benepoGetResponse.billingAmount !== undefined && newValue > this.benepoGetResponse.billingAmount) {
      errorType = 'Over';
    } else {
      errorType = 'NoError';
    }

    this.form.usePoint.errorType = errorType;
  }

  changeUsePointType(): void {
    switch (this.selectedUsePoint) {
      case 'All':
      case 'Partial':
        this.form.usePoint.value = this.getUsePointForAll;
        this.changeSpinner();
        break;

      case 'None':
        this.form.usePoint.value = 0;
        break;

      default:
        throw Error('invalid argument');
    }
  }

  changeSpinner(): void {
    this.validateUsePoint(this.form.usePoint.value);
  }

  clickStepDown(): void {
    if (this.disabledInputUsePoint || Number(this.form.usePoint.value) <= 0) return;

    this.form.usePoint.value = Number(this.form.usePoint.value) - 1;
    this.validateUsePoint(this.form.usePoint.value);
  }

  clickStepUp(): void {
    if (this.disabledInputUsePoint) return;

    this.form.usePoint.value = Number(this.form.usePoint.value) + 1;
    this.validateUsePoint(this.form.usePoint.value);
  }

  clickCancel(): void {
    this.$emit('close');
  }

  clickSubmit(): void {
    // validate.
    if (this.selectedUsePoint === 'Partial' && this.form.usePoint.errorType !== 'NoError') {
      return;
    }

    // call api.
    const config = OrderApiConfig.newInstance();
    const apiOrder = new DPApi(config);
    store.commit('setIsLoading', true);

    let benepoAmountTotal: number;
    switch (this.selectedUsePoint) {
      case 'All':
      case 'Partial':
        benepoAmountTotal = this.form.usePoint.value === '' ? 0 : this.form.usePoint.value;
        break;

      case 'None':
      default:
        benepoAmountTotal = 0;
        break;
    }

    const request: PutBenepoRequest = {
      benepoAmountTotal: benepoAmountTotal.toString(),
    };

    if (this.orderSite === 4) {
      apiOrder
        .putBenepoFellowTravelers(request)
        .then(() => {
          store.commit('setIsLoading', false);
          this.$store.commit('setBenp01SelectedUsePoint', this.selectedUsePoint);
          this.$emit('submit');
        })
        .catch((error) => {
          store.commit('setIsLoading', false);
          if (error.status !== 400) {
            this.$router.push({
              name: 'Erro01',
            });
          }
          return error.json();
        })
        .then((response) => {
          this.benepoErrorResponse = response;
        });
    } else {
      apiOrder
        .putBenepo(request)
        .then(() => {
          store.commit('setIsLoading', false);
          this.$store.commit('setBenp01SelectedUsePoint', this.selectedUsePoint);
          this.$emit('submit');
        })
        .catch((error) => {
          store.commit('setIsLoading', false);
          if (error.status !== 400) {
            this.$router.push({
              name: 'Erro01',
            });
          }
          return error.json();
        })
        .then((response) => {
          this.benepoErrorResponse = response;
        });
    }
  }
}
